import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, combineLatest } from 'rxjs';
import { map, mergeMap, take } from 'rxjs/operators';
import { HrService } from '../../../services/human-resources/hr.service';
import { Employee, PersonalEvent, PersonalEventType } from '../../../models/hr-models';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

// Create an interface that extends PersonalEvent to handle both formats
interface ApiPersonalEvent extends Partial<PersonalEvent> {
  // Snake case properties from API
  id?: number;
  employee_id?: number;
  event_type?: string;
  event_date?: string | Date;
  notification_sent?: boolean;
  created_at?: string;
  updated_at?: string;
}

@Component({
  selector: 'app-personal-tracking',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule,FontAwesomeModule],
  templateUrl: './personal-tracking.component.html',
  styleUrls: ['./personal-tracking.component.scss']
})
export class PersonalTrackingComponent implements OnInit {
  employees$: Observable<Employee[]>;
  upcomingBirthdays$: Observable<any[]>;
  upcomingAnniversaries$: Observable<any[]>;

  // Track active month for calendar view
  currentMonthDate = new Date();
  daysInMonth: (number | null)[] = [];
  monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Event form and modal
  eventForm: FormGroup;
  editingEvent: PersonalEvent | null = null;
  showEventModal = false;
  personalEventTypes = Object.values(PersonalEventType);

  // Icons
  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;

  constructor(
    private hrService: HrService,
    private fb: FormBuilder
  ) {
    this.employees$ = this.hrService.employees$;

    // Get upcoming birthdays for the next 30 days
    this.upcomingBirthdays$ = this.getUpcomingEvents(PersonalEventType.Birthday, 30);

    // Get upcoming work anniversaries for the next 30 days
    this.upcomingAnniversaries$ = this.getUpcomingEvents(PersonalEventType.WorkAnniversary, 30);

    // Initialize calendar days
    this.buildCalendarDays();

    // Initialize event form
    this.eventForm = this.fb.group({
      employeeId: [null, Validators.required],
      eventType: ['', Validators.required],
      eventDate: [null, Validators.required]
    });
  }

  ngOnInit(): void {
    // First load the basic data
    this.hrService.getEmployees().subscribe(() => {
      console.log('Employees loaded, syncing with personal events');
      
      // Then sync employee data with personal events (will re-fetch both)
      this.hrService.syncEmployeeDateEvents().subscribe({
        next: (success) => {
          console.log(`Successfully synced employee data with personal events: ${success}`);
          
          // After sync, refresh the actual displays
          this.refreshDisplays();
        },
        error: (err) => {
          console.error('Error during sync:', err);
          
          // Still try to refresh displays even if sync failed
          this.refreshDisplays();
        }
      });
    });
  }

  /**
   * Refresh all displayed data without syncing
   */
  private refreshDisplays(): void {
    // Get upcoming birthdays for the next 30 days
    this.upcomingBirthdays$ = this.getUpcomingEvents(PersonalEventType.Birthday, 30);

    // Get upcoming work anniversaries for the next 30 days
    this.upcomingAnniversaries$ = this.getUpcomingEvents(PersonalEventType.WorkAnniversary, 30);
    
    // Rebuild calendar to reflect changes
    this.buildCalendarDays();
  }

  /**
   * Refresh the events lists and sync with employee data
   */
  refreshEvents(): void {
    console.log('[refreshEvents] Refreshing event lists');
    
    // Show a loading indicator or message
    const loadingElement = document.createElement('div');
    loadingElement.className = 'sync-loading';
    loadingElement.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Syncing employee data...';
    document.body.appendChild(loadingElement);
    
    // Force synchronization of employee dates with personal events
    this.hrService.syncEmployeeDateEvents().subscribe({
      next: (success) => {
        console.log(`[refreshEvents] Sync completed with status: ${success}`);
        
        // Re-query the upcoming events
        this.upcomingBirthdays$ = this.getUpcomingEvents(PersonalEventType.Birthday, 30);
        this.upcomingAnniversaries$ = this.getUpcomingEvents(PersonalEventType.WorkAnniversary, 30);
        
        // Also rebuild calendar to reflect changes
        this.buildCalendarDays();
        
        // Log current month for calendar reference
        console.log('[refreshEvents] Current month for calendar:', 
          this.monthNames[this.currentMonthDate.getMonth()], 
          this.currentMonthDate.getFullYear());
        
        // Remove loading indicator
        document.body.removeChild(loadingElement);
        
        // Show success message
        alert('Employee data successfully synchronized with calendar events.');
      },
      error: (err) => {
        console.error('[refreshEvents] Error during sync:', err);
        
        // Remove loading indicator
        document.body.removeChild(loadingElement);
        
        // Show error message
        alert('There was an error synchronizing employee data. Please try again.');
      }
    });
  }

  /**
   * Open modal to add a new event
   */
  openAddEventModal(): void {
    this.editingEvent = null;
    this.eventForm.reset();
    this.showEventModal = true;
    
    // Add event listener for employee selection changes to warn about duplicates
    this.eventForm.get('employeeId')?.valueChanges.subscribe(employeeId => {
      if (employeeId) {
        this.eventForm.get('eventType')?.valueChanges.subscribe(eventType => {
          if (eventType) {
            // Check if this employee already has this type of event
            this.hrService.personalEvents$.pipe(take(1)).subscribe(events => {
              const existingEvent = events.find(event => {
                const apiEvent = event as ApiPersonalEvent;
                const eventEmployeeId = apiEvent.employeeId || apiEvent.employee_id;
                const eventTypeValue = apiEvent.eventType || apiEvent.event_type;
                return eventEmployeeId === employeeId && eventTypeValue === eventType;
              });
              
              if (existingEvent) {
                console.warn(`Employee already has a ${eventType} event`);
                alert(`Warning: This employee already has a ${eventType} event. Adding another one may cause duplicate entries.`);
              }
            });
          }
        });
      }
    });
  }

  /**
   * Open modal to add a new event with a pre-selected date
   */
  openAddEventModalWithDate(day: number): void {
    this.editingEvent = null;
    this.eventForm.reset();

    // Create a date object for the selected day
    // Use UTC to prevent timezone issues
    const selectedDate = new Date(Date.UTC(
      this.currentMonthDate.getFullYear(),
      this.currentMonthDate.getMonth(),
      day
    ));
    
    // Format the date as YYYY-MM-DD for the date input
    const formattedDate = selectedDate.toISOString().split('T')[0];
    
    console.log('Selected day:', day);
    console.log('Formatted date for form:', formattedDate);

    // Set the date in the form
    this.eventForm.patchValue({
      eventDate: formattedDate
    });

    this.showEventModal = true;
  }

  /**
   * Open modal to edit an existing event
   */
  editEvent(event: PersonalEvent): void {
    this.editingEvent = event;
    
    // Ensure we have a proper Date object
    const eventDate = new Date(event.date);
    // Format properly for the date input, ensuring we avoid timezone issues
    const formattedDate = eventDate.toISOString().split('T')[0];
    
    console.log('Editing event with date:', eventDate);
    console.log('Formatted date for form:', formattedDate);
    
    this.eventForm.setValue({
      employeeId: event.employeeId,
      eventType: event.eventType,
      eventDate: formattedDate
    });
    
    this.showEventModal = true;
  }

  /**
   * Close the event modal
   */
  closeEventModal(): void {
    this.showEventModal = false;
    this.eventForm.reset();
  }

  /**
   * Save the event (create or update)
   */
  saveEvent(): void {
    if (this.eventForm.invalid) {
      // Mark fields as touched to show validation errors
      Object.keys(this.eventForm.controls).forEach(key => {
        this.eventForm.get(key)?.markAsTouched();
      });
      return;
    }

    const formValue = this.eventForm.value;
    console.log('Form values:', formValue);
    
    // Create a date in UTC to avoid timezone issues
    const eventDateStr = formValue.eventDate;
    const [year, month, day] = eventDateStr.split('-').map((num: string) => parseInt(num, 10));
    
    // Make sure we're using the right approach based on event type
    // For birthdays and anniversaries, we need to store the actual birth/hire year
    let eventDate = new Date(Date.UTC(year, month - 1, day));
    
    console.log('Parsed event date:', eventDate, 'Event type:', formValue.eventType);
    console.log('ISO string:', eventDate.toISOString(), 'UTC string:', eventDate.toUTCString());

    const event: PersonalEvent = {
      id: this.editingEvent ? this.editingEvent.id : 0,
      employeeId: formValue.employeeId,
      eventType: formValue.eventType,
      date: eventDate,
      notificationSent: this.editingEvent ? this.editingEvent.notificationSent : false
    };

    if (this.editingEvent) {
      this.hrService.updatePersonalEvent(event).subscribe(
        (updatedEvent) => {
          console.log('Event updated successfully:', updatedEvent);
          this.closeEventModal();
          
          // Force refresh data from the server to get updated events
          this.hrService.getPersonalEvents().subscribe(() => {
            this.refreshEvents();
          });
        },
        (error: Error) => {
          console.error('Error updating event:', error);
          alert('There was an error updating the event. Please try again.');
        }
      );
    } else {
      this.hrService.addPersonalEvent(event).subscribe(
        (newEvent) => {
          console.log('Event added successfully:', newEvent);
          this.closeEventModal();
          
          // Force refresh data from the server to get updated events
          this.hrService.getPersonalEvents().subscribe(() => {
            this.refreshEvents();
          });
        },
        (error: Error) => {
          console.error('Error adding event:', error);
          alert('There was an error adding the event. Please try again.');
        }
      );
    }
  }

  /**
   * Delete a personal event
   */
  deleteEvent(id: number): void {
    if (confirm('Are you sure you want to delete this event?')) {
      this.hrService.deletePersonalEvent(id).subscribe(
        () => {
          this.refreshEvents();
        },
        (error: Error) => console.error('Error deleting event:', error)
      );
    }
  }

  /**
   * Get upcoming personal events (birthdays or anniversaries)
   */
  getUpcomingEvents(eventType: PersonalEventType, daysAhead: number): Observable<any[]> {
    return combineLatest([
      this.employees$,
      this.hrService.personalEvents$
    ]).pipe(
      map(([employees, events]) => {
        const today = new Date();
        const futureDate = new Date();
        futureDate.setDate(today.getDate() + daysAhead);

        // Debug to see what events we're getting
        console.log(`[getUpcomingEvents] Processing ${events.length} events for type ${eventType}`);
        if (events.length > 0) {
          console.log('[getUpcomingEvents] First event data:', JSON.stringify(events[0]));
        }

        // Filter events by type
        const filteredEvents = events.filter(event => {
          const apiEvent = event as ApiPersonalEvent;
          return apiEvent.eventType === eventType || apiEvent.event_type === eventType;
        });

        console.log(`[getUpcomingEvents] Found ${filteredEvents.length} events of type ${eventType}`);

        return filteredEvents.map(event => {
          const apiEvent = event as ApiPersonalEvent;
          // Extract the proper date field, handling both camelCase and snake_case formats
          const eventDateValue = apiEvent.date || apiEvent.event_date;
          let eventDate = new Date(eventDateValue as Date);
          
          // Make sure the date is valid
          if (isNaN(eventDate.getTime())) {
            console.error('Invalid date in event:', apiEvent);
            return null;
          }
          
          // Find the associated employee
          const employeeId = apiEvent.employeeId || apiEvent.employee_id;
          const employee = employees.find(emp => emp.id === employeeId);
          
          if (!employee) {
            console.error('Employee not found for event:', apiEvent);
            return null;
          }

          let label: string;
          let currentYearDate: Date;
          let daysUntil: number;

          // Current year's date for this event (just matching month/day)
          currentYearDate = new Date(today.getFullYear(), eventDate.getMonth(), eventDate.getDate());
          
          // If this date has already passed this year, use next year's date
          if (currentYearDate < today) {
            currentYearDate.setFullYear(today.getFullYear() + 1);
          }
          
          // Calculate days until event
          daysUntil = Math.floor((currentYearDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          
          // FIXED: Generate the appropriate label based on event type using event date from DB
          if (eventType === PersonalEventType.Birthday) {
            // For birthdays, get the original full date
            // Extract just the year from the event date which is the birth year
            const birthYear = eventDate.getFullYear();
            // Calculate age by comparing with the currentYearDate
            // If the event is in next year's calendar, add 1 to the age
            const nextYear = currentYearDate.getFullYear() > today.getFullYear() ? 1 : 0;
            const age = (today.getFullYear() - birthYear) + nextYear;
            
            // Debug
            console.log(`[getUpcomingEvents] Birthday: Birth year: ${birthYear}, Current year: ${today.getFullYear()}, Next year addition: ${nextYear}, Age: ${age}`);
            
            label = `Turning ${age}`;
          } else {
            // For work anniversaries, get original hire year
            const hireYear = eventDate.getFullYear();
            // Calculate years of service
            // If the event is in next year's calendar, add 1 to the years
            const nextYear = currentYearDate.getFullYear() > today.getFullYear() ? 1 : 0;
            const years = (today.getFullYear() - hireYear) + nextYear;
            
            // Debug
            console.log(`[getUpcomingEvents] Anniversary: Hire year: ${hireYear}, Current year: ${today.getFullYear()}, Next year addition: ${nextYear}, Years: ${years}`);
            
            label = `${years} Years`;
          }

          return {
            id: apiEvent.id,
            employee,
            eventDate: currentYearDate,
            label,
            daysUntil,
            originalDate: eventDate // Keep original date for debugging
          };
        })
        .filter(event => event !== null && event.daysUntil >= 0 && event.daysUntil <= daysAhead)
        .sort((a, b) => {
          if (a === null || b === null) return 0;
          return a.daysUntil - b.daysUntil;
        });
      })
    );
  }

  /**
   * Calendar navigation
   */
  previousMonth(): void {
    this.currentMonthDate.setMonth(this.currentMonthDate.getMonth() - 1);
    this.buildCalendarDays();
  }

  nextMonth(): void {
    this.currentMonthDate.setMonth(this.currentMonthDate.getMonth() + 1);
    this.buildCalendarDays();
  }

  /**
   * Build the days array for the calendar
   */
  buildCalendarDays(): void {
    const year = this.currentMonthDate.getFullYear();
    const month = this.currentMonthDate.getMonth();

    // Get the first day of the month
    const firstDay = new Date(year, month, 1);

    // Get the last day of the month
    const lastDay = new Date(year, month + 1, 0);

    // Calculate days to display from previous month to align the calendar
    const prevMonthDays = firstDay.getDay();

    // Calculate the total number of days in the current month
    const daysInMonth = lastDay.getDate();

    // Clear the days array
    this.daysInMonth = [];

    // Add days from previous month as null (just placeholders)
    for (let i = 0; i < prevMonthDays; i++) {
      this.daysInMonth.push(null);
    }

    // Add the days of the current month
    for (let i = 1; i <= daysInMonth; i++) {
      this.daysInMonth.push(i);
    }
  }

  /**
   * Export upcoming events to Excel file
   */
  exportUpcomingEvents(): void {
    // Default to 30 days ahead
    const daysAhead = 30;
    
    this.hrService.exportUpcomingEventsToExcel(daysAhead).subscribe({
      next: (blob: Blob) => {
        // Create a URL for the blob
        const url = window.URL.createObjectURL(blob);
        
        // Create an anchor element
        const a = document.createElement('a');
        a.href = url;
        a.download = `upcoming_events_${new Date().toISOString().split('T')[0]}.xlsx`;
        
        // Append to the document, click and remove
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      },
      error: (error) => {
        console.error('Error exporting upcoming events:', error);
        alert('There was an error exporting the upcoming events. Please try again later.');
      }
    });
  }

  /**
   * Get events for a specific day in the current month's calendar
   */
  getEventsForDay(day: number): Observable<{birthdays: any[], anniversaries: any[]}> {
    return combineLatest([
      this.hrService.personalEvents$,
      this.hrService.employees$
    ]).pipe(
      map(([events, employees]) => {
        const year = this.currentMonthDate.getFullYear();
        const month = this.currentMonthDate.getMonth();
        const checkDate = new Date(year, month, day);
        const formattedCheckDate = this.formatDateForComparison(checkDate);

        // First get events from personal_events table
        const tableEvents = events.filter(event => {
          const apiEvent = event as ApiPersonalEvent;
          // Handle both camelCase and snake_case date properties
          const eventDateValue = apiEvent.date || apiEvent.event_date;
          const eventDate = new Date(eventDateValue as Date);
          return this.formatDateForComparison(eventDate) === formattedCheckDate;
        });

        // Prepare employee-based results
        const birthdays: any[] = [];
        const anniversaries: any[] = [];

        // Process personal events from the database
        tableEvents.forEach(event => {
          const apiEvent = event as ApiPersonalEvent;
          // Handle both camelCase and snake_case property names
          const employeeId = apiEvent.employeeId || apiEvent.employee_id;
          const eventType = apiEvent.eventType || apiEvent.event_type;
          const employee = employees.find(emp => emp.id === employeeId);
          
          if (employee) {
            const eventObj = { 
              ...apiEvent, 
              ...employee, 
              name: employee.name,
              id: event.id,  // Keep the personal event id for edit/delete actions
              employeeId    // Keep the employee id
            };
            
            if (eventType === PersonalEventType.Birthday) {
              birthdays.push(eventObj);
            } else if (eventType === PersonalEventType.WorkAnniversary) {
              anniversaries.push(eventObj);
            }
          }
        });

        return { birthdays, anniversaries };
      })
    );
  }

  /**
   * Format a date as MM-DD for comparison (ignoring year)
   */
  private formatDateForComparison(date: Date): string {
    // Ensure we have a valid date
    if (!date || isNaN(date.getTime())) {
      console.error('Invalid date passed to formatDateForComparison:', date);
      return '';
    }
    
    const month = date.getMonth();
    const day = date.getDate();
    
    // Debug logs
    console.log(`[formatDateForComparison] Formatting date: ${date}, Month: ${month}, Day: ${day}`);
    
    return `${month}-${day}`;
  }

  /**
   * Edit an event by employee ID and event type
   */
  editEventByEmployeeId(employeeId: number, eventType: string): void {
    this.hrService.personalEvents$.pipe(
      map(events => events.find(event => {
        const apiEvent = event as ApiPersonalEvent;
        return (apiEvent.employeeId === employeeId || apiEvent.employee_id === employeeId) && 
               (apiEvent.eventType === eventType || apiEvent.event_type === eventType);
      })),
      // Only take the first value and then complete
      take(1)
    ).subscribe(event => {
      if (event) {
        this.editEvent(event as PersonalEvent);
      }
    });
  }

  /**
   * Delete an event by employee ID and event type
   */
  deleteEventByEmployeeId(employeeId: number, eventType: string): void {
    this.hrService.personalEvents$.pipe(
      map(events => events.find(event => {
        const apiEvent = event as ApiPersonalEvent;
        return (apiEvent.employeeId === employeeId || apiEvent.employee_id === employeeId) && 
               (apiEvent.eventType === eventType || apiEvent.event_type === eventType);
      })),
      // Only take the first value and then complete
      take(1)
    ).subscribe(event => {
      if (event) {
        this.deleteEvent(event.id);
      }
    });
  }

  /**
   * Format date for display
   */
  formatDate(date: Date): string {
    return new Date(date).toLocaleDateString();
  }
}
